// =============================================
// AI Assistant Tools for OpenAI Function Calling
// Server-side tool definitions + execution layer
// =============================================

import { createClient } from '@supabase/supabase-js';

// ---------------------------------------------------------------------------
// Supabase Admin Client (service role — server-side only)
// ---------------------------------------------------------------------------

const supabaseAdmin = createClient(
  process.env.NEXT_PUBLIC_SUPABASE_URL!,
  process.env.SUPABASE_SERVICE_ROLE_KEY!,
  {
    auth: {
      autoRefreshToken: false,
      persistSession: false,
    },
  }
);

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ToolContext {
  userId: string;
  userRole: string;
}

type ToolResult = { success: true; data: unknown } | { success: false; error: string };

// ---------------------------------------------------------------------------
// ASSISTANT_TOOLS — OpenAI function-calling definitions
// ---------------------------------------------------------------------------

export const ASSISTANT_TOOLS: Array<{
  type: 'function';
  function: {
    name: string;
    description: string;
    parameters: Record<string, unknown>;
  };
}> = [
  // 1. listar_clientes
  {
    type: 'function',
    function: {
      name: 'listar_clientes',
      description:
        'Lista los clientes asignados al asesor. Úsalo cuando el usuario pregunte por sus clientes, empresas, o necesite buscar un cliente específico.',
      parameters: {
        type: 'object',
        properties: {
          filtro: {
            type: 'string',
            description: 'Filtrar por nombre o NIF del cliente',
          },
        },
        required: [],
      },
    },
  },

  // 2. listar_facturas
  {
    type: 'function',
    function: {
      name: 'listar_facturas',
      description:
        'Lista facturas con filtros opcionales. Úsalo cuando el usuario pregunte por facturas, ventas, cobros o necesite ver el estado de facturación.',
      parameters: {
        type: 'object',
        properties: {
          cliente_id: {
            type: 'string',
            description: 'ID del cliente para filtrar facturas',
          },
          estado: {
            type: 'string',
            enum: ['borrador', 'emitida', 'pagada', 'vencida'],
            description: 'Estado de la factura',
          },
          limite: {
            type: 'number',
            description: 'Número máximo de facturas a devolver (por defecto 20)',
          },
        },
        required: [],
      },
    },
  },

  // 3. crear_factura
  {
    type: 'function',
    function: {
      name: 'crear_factura',
      description:
        'Crea una factura en borrador para un cliente. Úsalo cuando el usuario quiera crear, generar o emitir una nueva factura.',
      parameters: {
        type: 'object',
        properties: {
          cliente_id: {
            type: 'string',
            description: 'ID, nombre o NIF del cliente al que se emite la factura',
          },
          lineas: {
            type: 'array',
            description: 'Líneas de la factura',
            items: {
              type: 'object',
              properties: {
                concepto: {
                  type: 'string',
                  description: 'Descripción del concepto o servicio',
                },
                cantidad: {
                  type: 'number',
                  description: 'Cantidad de unidades',
                },
                precio_unitario: {
                  type: 'number',
                  description: 'Precio por unidad en euros',
                },
                iva_porcentaje: {
                  type: 'number',
                  description: 'Porcentaje de IVA (por defecto 21)',
                },
              },
              required: ['concepto', 'cantidad', 'precio_unitario'],
            },
          },
          notas: {
            type: 'string',
            description: 'Notas adicionales para la factura',
          },
        },
        required: ['cliente_id', 'lineas'],
      },
    },
  },

  // 4. buscar_documentos
  {
    type: 'function',
    function: {
      name: 'buscar_documentos',
      description:
        'Busca documentos subidos por los clientes. Úsalo cuando el usuario pregunte por documentos, archivos, facturas recibidas o cualquier fichero subido.',
      parameters: {
        type: 'object',
        properties: {
          cliente_id: {
            type: 'string',
            description: 'ID, nombre o NIF del cliente para filtrar documentos',
          },
          tipo: {
            type: 'string',
            description: 'Tipo de documento (emitida_pdf, recibida_pdf, emitida_excel, recibida_excel, otro)',
          },
          estado: {
            type: 'string',
            description: 'Estado del documento (pendiente, procesado_ia, validado, contabilizado, incidencia, rechazado)',
          },
          texto: {
            type: 'string',
            description: 'Texto libre para buscar en el nombre del documento',
          },
          limite: {
            type: 'number',
            description: 'Número máximo de documentos a devolver (por defecto 20)',
          },
        },
        required: [],
      },
    },
  },

  // 5. procesar_ocr
  {
    type: 'function',
    function: {
      name: 'procesar_ocr',
      description:
        'Procesa un documento con OCR/IA para extraer datos estructurados. Úsalo cuando el usuario quiera analizar, leer o extraer información de un documento.',
      parameters: {
        type: 'object',
        properties: {
          documento_id: {
            type: 'string',
            description: 'ID del documento a procesar',
          },
        },
        required: ['documento_id'],
      },
    },
  },

  // 6. consulta_financiera
  {
    type: 'function',
    function: {
      name: 'consulta_financiera',
      description:
        'Consulta el resumen financiero de un cliente. Úsalo cuando el usuario pregunte por ingresos, gastos, situación financiera o resumen económico de un cliente.',
      parameters: {
        type: 'object',
        properties: {
          cliente_id: {
            type: 'string',
            description: 'ID, nombre o NIF del cliente del que se quiere el resumen financiero',
          },
        },
        required: ['cliente_id'],
      },
    },
  },

  // 7. listar_incidencias
  {
    type: 'function',
    function: {
      name: 'listar_incidencias',
      description:
        'Lista las incidencias abiertas. Úsalo cuando el usuario pregunte por problemas, incidencias, errores o issues pendientes. Cada incidencia incluye el primer mensaje como resumen.',
      parameters: {
        type: 'object',
        properties: {
          cliente_id: {
            type: 'string',
            description: 'ID, nombre o NIF del cliente para filtrar incidencias',
          },
          estado: {
            type: 'string',
            enum: ['nueva', 'en_revision', 'resuelta', 'cerrada'],
            description: 'Estado de la incidencia',
          },
        },
        required: [],
      },
    },
  },

  // 8. crear_incidencia
  {
    type: 'function',
    function: {
      name: 'crear_incidencia',
      description:
        'Crea una nueva incidencia para un cliente. Úsalo cuando el usuario quiera reportar un problema, crear un ticket o registrar una incidencia. El mensaje proporcionado se registra como primer mensaje de la incidencia.',
      parameters: {
        type: 'object',
        properties: {
          cliente_id: {
            type: 'string',
            description: 'ID, nombre o NIF del cliente asociado a la incidencia',
          },
          mensaje: {
            type: 'string',
            description: 'Descripción detallada del problema o motivo de la incidencia',
          },
          documento_id: {
            type: 'string',
            description: 'ID del documento relacionado (opcional)',
          },
        },
        required: ['cliente_id', 'mensaje'],
      },
    },
  },
];

// ---------------------------------------------------------------------------
// executeToolCall — dispatcher
// ---------------------------------------------------------------------------

export async function executeToolCall(
  toolName: string,
  args: Record<string, unknown>,
  context: ToolContext
): Promise<ToolResult> {
  try {
    switch (toolName) {
      case 'listar_clientes':
        return await ejecutarListarClientes(args, context);
      case 'listar_facturas':
        return await ejecutarListarFacturas(args, context);
      case 'crear_factura':
        return await ejecutarCrearFactura(args, context);
      case 'buscar_documentos':
        return await ejecutarBuscarDocumentos(args, context);
      case 'procesar_ocr':
        return await ejecutarProcesarOcr(args, context);
      case 'consulta_financiera':
        return await ejecutarConsultaFinanciera(args, context);
      case 'listar_incidencias':
        return await ejecutarListarIncidencias(args, context);
      case 'crear_incidencia':
        return await ejecutarCrearIncidencia(args, context);
      default:
        return { success: false, error: `Herramienta desconocida: ${toolName}` };
    }
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    console.error(`[ai-tools] Error executing ${toolName}:`, message);
    return { success: false, error: message };
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Round monetary value to 2 decimals. */
function round2(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

/**
 * Get the list of cliente IDs assigned to an asesor.
 * Returns all cliente IDs if the user is admin.
 */
async function getClienteIdsForAsesor(userId: string, userRole: string): Promise<string[]> {
  if (userRole === 'admin') {
    const { data } = await supabaseAdmin
      .from('clientes')
      .select('id');
    return data?.map((c) => c.id) ?? [];
  }

  const { data: asignaciones } = await supabaseAdmin
    .from('asesor_cliente')
    .select('cliente_id')
    .eq('asesor_id', userId);

  return asignaciones?.map((a) => a.cliente_id) ?? [];
}

/**
 * Resolve a client identifier (UUID, name, or NIF) to a valid client UUID.
 * Returns the UUID if found and allowed, or null otherwise.
 */
async function resolveClienteId(
  input: string,
  allowedClienteIds: string[]
): Promise<string | null> {
  // If the input is already a valid UUID in the allowed list, return it directly
  if (allowedClienteIds.includes(input)) {
    return input;
  }

  // Otherwise, search by razon_social or NIF within the allowed clients
  const { data } = await supabaseAdmin
    .from('clientes')
    .select('id')
    .in('id', allowedClienteIds)
    .or(`razon_social.ilike.%${input}%,nif.ilike.%${input}%`)
    .limit(1);

  if (data && data.length > 0) {
    return data[0].id;
  }

  return null;
}

// ---------------------------------------------------------------------------
// 1. listar_clientes
// ---------------------------------------------------------------------------

async function ejecutarListarClientes(
  args: Record<string, unknown>,
  context: ToolContext
): Promise<ToolResult> {
  const filtro = typeof args.filtro === 'string' ? args.filtro.trim() : '';

  // Get assigned client IDs
  const clienteIds = await getClienteIdsForAsesor(context.userId, context.userRole);

  if (clienteIds.length === 0) {
    return { success: true, data: [] };
  }

  let query = supabaseAdmin
    .from('clientes')
    .select('id, razon_social, nif, estado, email')
    .in('id', clienteIds)
    .order('razon_social', { ascending: true });

  if (filtro) {
    query = query.or(`razon_social.ilike.%${filtro}%,nif.ilike.%${filtro}%`);
  }

  const { data, error } = await query;

  if (error) {
    return { success: false, error: error.message };
  }

  const clientes = (data ?? []).map((c) => ({
    id: c.id,
    nombre: c.razon_social,
    nif: c.nif,
    estado: c.estado,
    email: c.email,
  }));

  return { success: true, data: clientes };
}

// ---------------------------------------------------------------------------
// 2. listar_facturas
// ---------------------------------------------------------------------------

async function ejecutarListarFacturas(
  args: Record<string, unknown>,
  context: ToolContext
): Promise<ToolResult> {
  const clienteId = typeof args.cliente_id === 'string' ? args.cliente_id : undefined;
  const estado = typeof args.estado === 'string' ? args.estado : undefined;
  const limite = typeof args.limite === 'number' ? Math.min(args.limite, 100) : 20;

  // Scope to asesor's clients
  const clienteIds = await getClienteIdsForAsesor(context.userId, context.userRole);
  if (clienteIds.length === 0) {
    return { success: true, data: [] };
  }

  let query = supabaseAdmin
    .from('invoices')
    .select('id, numero, cliente_nombre, total_factura, estado, fecha_emision, empresa_id')
    .in('empresa_id', clienteIds)
    .order('fecha_emision', { ascending: false })
    .limit(limite);

  if (clienteId) {
    query = query.eq('empresa_id', clienteId);
  }

  if (estado) {
    query = query.eq('estado', estado);
  }

  const { data, error } = await query;

  if (error) {
    return { success: false, error: error.message };
  }

  const facturas = (data ?? []).map((f) => ({
    id: f.id,
    numero: f.numero,
    cliente_nombre: f.cliente_nombre,
    total_factura: f.total_factura,
    estado: f.estado,
    fecha_emision: f.fecha_emision,
  }));

  return { success: true, data: facturas };
}

// ---------------------------------------------------------------------------
// 3. crear_factura
// ---------------------------------------------------------------------------

interface LineaInput {
  concepto: string;
  cantidad: number;
  precio_unitario: number;
  iva_porcentaje?: number;
}

async function ejecutarCrearFactura(
  args: Record<string, unknown>,
  context: ToolContext
): Promise<ToolResult> {
  const clienteIdInput = args.cliente_id as string | undefined;
  const lineas = args.lineas as LineaInput[] | undefined;
  const notas = typeof args.notas === 'string' ? args.notas : undefined;

  if (!clienteIdInput || !lineas || !Array.isArray(lineas) || lineas.length === 0) {
    return { success: false, error: 'Se requiere cliente_id y al menos una línea de factura.' };
  }

  // Resolve client by ID, name, or NIF
  const clienteIds = await getClienteIdsForAsesor(context.userId, context.userRole);
  const clienteId = await resolveClienteId(clienteIdInput, clienteIds);
  if (!clienteId) {
    return { success: false, error: 'Cliente no encontrado o no tienes acceso a este cliente.' };
  }

  // Get client data for the invoice
  const { data: cliente, error: clienteError } = await supabaseAdmin
    .from('clientes')
    .select('id, razon_social, nif, email, direccion_fiscal')
    .eq('id', clienteId)
    .single();

  if (clienteError || !cliente) {
    return { success: false, error: 'Cliente no encontrado.' };
  }

  // Find or create a default invoice series for this client
  let serieId: string;
  const { data: existingSerie } = await supabaseAdmin
    .from('invoice_series')
    .select('id, siguiente_numero')
    .eq('empresa_id', clienteId)
    .eq('activa', true)
    .order('created_at', { ascending: true })
    .limit(1)
    .maybeSingle();

  if (existingSerie) {
    serieId = existingSerie.id;
  } else {
    // Create a default series
    const { data: newSerie, error: serieError } = await supabaseAdmin
      .from('invoice_series')
      .insert({
        empresa_id: clienteId,
        codigo: 'F',
        nombre: 'Facturas Ordinarias',
        tipo: 'ordinaria',
        prefijo: `${new Date().getFullYear()}-`,
        siguiente_numero: 1,
        formato_numero: '{prefijo}{numero:05d}',
        activa: true,
        verifactu_habilitado: false,
      })
      .select('id, siguiente_numero')
      .single();

    if (serieError || !newSerie) {
      return { success: false, error: `Error al crear serie de facturación: ${serieError?.message}` };
    }
    serieId = newSerie.id;
  }

  // Get the next invoice number
  const { data: serie } = await supabaseAdmin
    .from('invoice_series')
    .select('siguiente_numero, prefijo, formato_numero')
    .eq('id', serieId)
    .single();

  const siguienteNumero = serie?.siguiente_numero ?? 1;
  const prefijo = serie?.prefijo ?? '';
  const numeroFactura = `${prefijo}${String(siguienteNumero).padStart(5, '0')}`;

  // Calculate line totals
  let baseImponible = 0;
  let totalImpuestos = 0;

  const calculatedLines = lineas.map((linea, index) => {
    const ivaPorcentaje = linea.iva_porcentaje ?? 21;
    const baseLinea = round2(linea.cantidad * linea.precio_unitario);
    const cuotaIva = round2(baseLinea * ivaPorcentaje / 100);

    baseImponible = round2(baseImponible + baseLinea);
    totalImpuestos = round2(totalImpuestos + cuotaIva);

    return {
      orden: index + 1,
      concepto: linea.concepto,
      cantidad: linea.cantidad,
      precio_unitario: linea.precio_unitario,
      descuento_porcentaje: 0,
      base_linea: baseLinea,
      tipo_iva: 'IVA_GENERAL',
      porcentaje_iva: ivaPorcentaje,
      cuota_iva: cuotaIva,
      recargo_porcentaje: 0,
      recargo_cuota: 0,
      retencion_porcentaje: 0,
      retencion_cuota: 0,
      es_suplido: false,
    };
  });

  const totalFactura = round2(baseImponible + totalImpuestos);

  // Insert the invoice
  const { data: invoice, error: invoiceError } = await supabaseAdmin
    .from('invoices')
    .insert({
      empresa_id: clienteId,
      serie_id: serieId,
      numero: numeroFactura,
      tipo: 'ordinaria',
      estado: 'borrador',
      cliente_nombre: cliente.razon_social,
      cliente_nif: cliente.nif,
      cliente_direccion: cliente.direccion_fiscal,
      cliente_email: cliente.email,
      fecha_emision: new Date().toISOString(),
      moneda: 'EUR',
      tipo_cambio: 1,
      base_imponible: baseImponible,
      total_impuestos: totalImpuestos,
      total_retenciones: 0,
      total_suplidos: 0,
      total_factura: totalFactura,
      tipo_operacion: 'nacional',
      clave_regimen: 'general',
      es_recurrente: false,
      verifactu_enviada: false,
      face_enviada: false,
      email_enviado: false,
      pdf_generado: false,
      notas: notas ?? null,
      created_by: context.userId,
    })
    .select('id')
    .single();

  if (invoiceError || !invoice) {
    return { success: false, error: `Error al crear factura: ${invoiceError?.message}` };
  }

  // Insert invoice lines
  const lineInserts = calculatedLines.map((line) => ({
    invoice_id: invoice.id,
    ...line,
  }));

  const { error: linesError } = await supabaseAdmin
    .from('invoice_lines')
    .insert(lineInserts);

  if (linesError) {
    // Rollback: delete the invoice if lines fail
    await supabaseAdmin.from('invoices').delete().eq('id', invoice.id);
    return { success: false, error: `Error al crear líneas de factura: ${linesError.message}` };
  }

  // Increment the series number
  await supabaseAdmin
    .from('invoice_series')
    .update({ siguiente_numero: siguienteNumero + 1 })
    .eq('id', serieId);

  return {
    success: true,
    data: {
      id: invoice.id,
      numero_factura: numeroFactura,
      total: totalFactura,
      estado: 'borrador',
    },
  };
}

// ---------------------------------------------------------------------------
// 4. buscar_documentos
// ---------------------------------------------------------------------------

async function ejecutarBuscarDocumentos(
  args: Record<string, unknown>,
  context: ToolContext
): Promise<ToolResult> {
  const clienteIdInput = typeof args.cliente_id === 'string' ? args.cliente_id : undefined;
  const tipo = typeof args.tipo === 'string' ? args.tipo : undefined;
  const estado = typeof args.estado === 'string' ? args.estado : undefined;
  const texto = typeof args.texto === 'string' ? args.texto.trim() : '';
  const limite = typeof args.limite === 'number' ? Math.min(args.limite, 100) : 20;

  // Scope to asesor's clients
  const clienteIds = await getClienteIdsForAsesor(context.userId, context.userRole);
  if (clienteIds.length === 0) {
    return { success: true, data: [] };
  }

  // Resolve client filter by ID, name, or NIF
  const clienteId = clienteIdInput
    ? await resolveClienteId(clienteIdInput, clienteIds)
    : undefined;

  let query = supabaseAdmin
    .from('documentos')
    .select('id, nombre, tipo, estado, fecha_subida, cliente_id, clientes!inner(razon_social)')
    .in('cliente_id', clienteIds)
    .order('fecha_subida', { ascending: false })
    .limit(limite);

  if (clienteId) {
    query = query.eq('cliente_id', clienteId);
  }

  if (tipo) {
    query = query.eq('tipo', tipo);
  }

  if (estado) {
    query = query.eq('estado', estado);
  }

  if (texto) {
    query = query.ilike('nombre', `%${texto}%`);
  }

  const { data, error } = await query;

  if (error) {
    return { success: false, error: error.message };
  }

  const documentos = (data ?? []).map((doc: Record<string, unknown>) => {
    const clienteData = doc.clientes as { razon_social: string } | null;
    return {
      id: doc.id,
      nombre_archivo: doc.nombre,
      tipo: doc.tipo,
      estado: doc.estado,
      fecha_subida: doc.fecha_subida,
      empresa_nombre: clienteData?.razon_social ?? null,
    };
  });

  return { success: true, data: documentos };
}

// ---------------------------------------------------------------------------
// 5. procesar_ocr
// ---------------------------------------------------------------------------

async function ejecutarProcesarOcr(
  args: Record<string, unknown>,
  _context: ToolContext
): Promise<ToolResult> {
  const documentoId = args.documento_id as string | undefined;

  if (!documentoId) {
    return { success: false, error: 'Se requiere documento_id.' };
  }

  // Get document metadata
  const { data: doc, error: docError } = await supabaseAdmin
    .from('documentos')
    .select('id, nombre, url_archivo, tipo, ocr_status, ocr_result')
    .eq('id', documentoId)
    .single();

  if (docError || !doc) {
    return { success: false, error: 'Documento no encontrado.' };
  }

  // If already processed, return cached result
  if (doc.ocr_status === 'completed' && doc.ocr_result) {
    const cached = doc.ocr_result as Record<string, unknown>;
    return {
      success: true,
      data: {
        tipo_documento: cached.tipo_documento ?? cached.tipo ?? null,
        numero: cached.numero_documento ?? null,
        emisor: cached.emisor ?? null,
        receptor: cached.receptor ?? null,
        base_imponible: cached.base_imponible ?? null,
        iva_total: cached.iva_total ?? null,
        total: cached.total ?? null,
        confianza: cached.confianza ?? null,
      },
    };
  }

  // Resolve the document URL
  const urlArchivo = doc.url_archivo as string;
  if (!urlArchivo) {
    return { success: false, error: 'El documento no tiene archivo asociado.' };
  }

  let fullUrl = urlArchivo;
  if (!urlArchivo.startsWith('http://') && !urlArchivo.startsWith('https://')) {
    fullUrl = `${process.env.NEXT_PUBLIC_SUPABASE_URL}/storage/v1/object/public/documentos/${urlArchivo}`;
  }

  // Mark as processing
  await supabaseAdmin
    .from('documentos')
    .update({ ocr_status: 'processing' })
    .eq('id', documentoId);

  // Call OpenAI Vision API
  const openaiKey = process.env.OPENAI_API_KEY;
  if (!openaiKey) {
    await supabaseAdmin
      .from('documentos')
      .update({ ocr_status: 'failed', ocr_error: 'OPENAI_API_KEY no configurada.' })
      .eq('id', documentoId);
    return { success: false, error: 'OPENAI_API_KEY no configurada.' };
  }

  const ocrPrompt = `Analiza este documento financiero/contable y extrae la siguiente información en formato JSON:
{
  "tipo_documento": "factura|recibo|nomina|contrato|otro",
  "numero_documento": "número de factura/recibo si existe",
  "fecha_emision": "YYYY-MM-DD",
  "emisor": {
    "nombre": "nombre empresa emisora",
    "cif": "CIF/NIF"
  },
  "receptor": {
    "nombre": "nombre empresa receptora",
    "cif": "CIF/NIF"
  },
  "base_imponible": 0.00,
  "iva_total": 0.00,
  "total": 0.00,
  "confianza": 0.95
}

Si algún campo no es visible o legible, pon null. El campo "confianza" es tu estimación de 0 a 1 de la precisión de la extracción.
Responde SOLO con el JSON, sin texto adicional.`;

  const openaiResponse = await fetch('https://api.openai.com/v1/chat/completions', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${openaiKey}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      model: 'gpt-4o',
      messages: [
        {
          role: 'user',
          content: [
            { type: 'text', text: ocrPrompt },
            {
              type: 'image_url',
              image_url: {
                url: fullUrl,
                detail: 'high',
              },
            },
          ],
        },
      ],
      max_tokens: 2000,
      temperature: 0.1,
    }),
  });

  if (!openaiResponse.ok) {
    const errText = await openaiResponse.text();
    await supabaseAdmin
      .from('documentos')
      .update({ ocr_status: 'failed', ocr_error: errText })
      .eq('id', documentoId);
    return { success: false, error: `Error de OpenAI: ${errText}` };
  }

  const openaiData = (await openaiResponse.json()) as {
    choices: Array<{ message: { content: string } }>;
  };

  const rawContent = openaiData.choices[0]?.message?.content ?? '';

  // Parse the JSON response
  let extractedData: Record<string, unknown>;
  try {
    const jsonMatch = rawContent.match(/\{[\s\S]*\}/);
    if (!jsonMatch) {
      throw new Error('No se encontró JSON en la respuesta de OpenAI.');
    }
    extractedData = JSON.parse(jsonMatch[0]) as Record<string, unknown>;
  } catch {
    await supabaseAdmin
      .from('documentos')
      .update({ ocr_status: 'failed', ocr_error: 'Error al parsear respuesta de IA.' })
      .eq('id', documentoId);
    return { success: false, error: 'Error al parsear la respuesta de la IA.' };
  }

  // Save OCR result
  await supabaseAdmin
    .from('documentos')
    .update({
      ocr_status: 'completed',
      ocr_result: extractedData,
      ocr_error: null,
      ocr_processed_at: new Date().toISOString(),
    })
    .eq('id', documentoId);

  return {
    success: true,
    data: {
      tipo_documento: extractedData.tipo_documento ?? null,
      numero: extractedData.numero_documento ?? null,
      emisor: extractedData.emisor ?? null,
      receptor: extractedData.receptor ?? null,
      base_imponible: extractedData.base_imponible ?? null,
      iva_total: extractedData.iva_total ?? null,
      total: extractedData.total ?? null,
      confianza: extractedData.confianza ?? null,
    },
  };
}

// ---------------------------------------------------------------------------
// 6. consulta_financiera
// ---------------------------------------------------------------------------

async function ejecutarConsultaFinanciera(
  args: Record<string, unknown>,
  context: ToolContext
): Promise<ToolResult> {
  const clienteIdInput = args.cliente_id as string | undefined;

  if (!clienteIdInput) {
    return { success: false, error: 'Se requiere cliente_id.' };
  }

  // Resolve client by ID, name, or NIF
  const clienteIds = await getClienteIdsForAsesor(context.userId, context.userRole);
  const clienteId = await resolveClienteId(clienteIdInput, clienteIds);
  if (!clienteId) {
    return { success: false, error: 'Cliente no encontrado o no tienes acceso a este cliente.' };
  }

  // Aggregate invoices
  const { data: invoices, error: invError } = await supabaseAdmin
    .from('invoices')
    .select('id, total_factura, estado, fecha_emision')
    .eq('empresa_id', clienteId);

  if (invError) {
    return { success: false, error: invError.message };
  }

  const allInvoices = invoices ?? [];
  const ingresosTotal = round2(
    allInvoices
      .filter((i) => i.estado === 'pagada' || i.estado === 'emitida')
      .reduce((sum, i) => sum + (Number(i.total_factura) || 0), 0)
  );

  const facturasPendientes = allInvoices.filter(
    (i) => i.estado === 'emitida' || i.estado === 'borrador'
  ).length;

  const facturasPagadas = allInvoices.filter((i) => i.estado === 'pagada').length;

  // Aggregate documents (gastos from received documents)
  const { data: documentos, error: docError } = await supabaseAdmin
    .from('documentos')
    .select('id, tipo, datos_ia, fecha_subida')
    .eq('cliente_id', clienteId);

  if (docError) {
    return { success: false, error: docError.message };
  }

  const allDocs = documentos ?? [];
  const documentosTotal = allDocs.length;

  // Calculate gastos from received invoices with AI-extracted data
  let gastosTotal = 0;
  for (const doc of allDocs) {
    if (doc.tipo === 'recibida_pdf' || doc.tipo === 'recibida_excel') {
      const datosIA = doc.datos_ia as Record<string, unknown> | null;
      if (datosIA) {
        const total = Number(datosIA.totalFactura ?? datosIA.importeTotal ?? datosIA.total ?? 0);
        gastosTotal = round2(gastosTotal + total);
      }
    }
  }

  // Find most recent activity
  const allDates: string[] = [];
  for (const inv of allInvoices) {
    if (inv.fecha_emision) allDates.push(String(inv.fecha_emision));
  }
  for (const doc of allDocs) {
    if (doc.fecha_subida) allDates.push(String(doc.fecha_subida));
  }
  allDates.sort((a, b) => b.localeCompare(a));
  const ultimaActividad = allDates[0] ?? null;

  return {
    success: true,
    data: {
      ingresos_total: ingresosTotal,
      gastos_total: gastosTotal,
      facturas_pendientes: facturasPendientes,
      facturas_pagadas: facturasPagadas,
      documentos_total: documentosTotal,
      ultima_actividad: ultimaActividad,
    },
  };
}

// ---------------------------------------------------------------------------
// 7. listar_incidencias
// ---------------------------------------------------------------------------

async function ejecutarListarIncidencias(
  args: Record<string, unknown>,
  context: ToolContext
): Promise<ToolResult> {
  const clienteIdInput = typeof args.cliente_id === 'string' ? args.cliente_id : undefined;
  const estado = typeof args.estado === 'string' ? args.estado : undefined;

  // Scope to asesor's clients
  const clienteIds = await getClienteIdsForAsesor(context.userId, context.userRole);
  if (clienteIds.length === 0) {
    return { success: true, data: [] };
  }

  // Resolve client filter by ID, name, or NIF
  const clienteId = clienteIdInput
    ? await resolveClienteId(clienteIdInput, clienteIds)
    : undefined;

  let query = supabaseAdmin
    .from('incidencias')
    .select('id, estado, origen, fecha_creacion, fecha_ultima_respuesta, cliente_id, clientes!inner(razon_social), mensajes_incidencia(mensaje, fecha)')
    .in('cliente_id', clienteIds)
    .order('fecha_creacion', { ascending: false });

  if (clienteId) {
    query = query.eq('cliente_id', clienteId);
  }

  if (estado) {
    query = query.eq('estado', estado);
  }

  const { data, error } = await query;

  if (error) {
    return { success: false, error: error.message };
  }

  const incidencias = (data ?? []).map((inc: Record<string, unknown>) => {
    const clienteData = inc.clientes as { razon_social: string } | null;
    const mensajes = inc.mensajes_incidencia as Array<{ mensaje: string; fecha: string }> | null;
    // Sort messages by date ascending to get the first one as summary
    const sortedMensajes = (mensajes ?? []).sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime());
    const primerMensaje = sortedMensajes[0]?.mensaje ?? null;
    return {
      id: inc.id,
      resumen: primerMensaje ? (primerMensaje.length > 120 ? primerMensaje.substring(0, 120) + '...' : primerMensaje) : null,
      estado: inc.estado,
      origen: inc.origen,
      fecha_creacion: inc.fecha_creacion,
      total_mensajes: sortedMensajes.length,
      cliente_nombre: clienteData?.razon_social ?? null,
    };
  });

  return { success: true, data: incidencias };
}

// ---------------------------------------------------------------------------
// 8. crear_incidencia
// ---------------------------------------------------------------------------

async function ejecutarCrearIncidencia(
  args: Record<string, unknown>,
  context: ToolContext
): Promise<ToolResult> {
  const clienteIdInput = args.cliente_id as string | undefined;
  const mensaje = args.mensaje as string | undefined;
  const documentoId = args.documento_id as string | undefined;

  if (!clienteIdInput || !mensaje) {
    return { success: false, error: 'Se requiere cliente_id y mensaje.' };
  }

  // Resolve client by ID, name, or NIF
  const clienteIds = await getClienteIdsForAsesor(context.userId, context.userRole);
  const clienteId = await resolveClienteId(clienteIdInput, clienteIds);
  if (!clienteId) {
    return { success: false, error: 'Cliente no encontrado o no tienes acceso a este cliente.' };
  }

  // 1. Create the incidencia record
  const { data: incidencia, error: incError } = await supabaseAdmin
    .from('incidencias')
    .insert({
      documento_id: documentoId || null,
      cliente_id: clienteId,
      asesor_id: context.userId,
      estado: 'nueva',
      origen: 'asesor',
      fecha_creacion: new Date().toISOString(),
    })
    .select('id')
    .single();

  if (incError) {
    return { success: false, error: `Error al crear incidencia: ${incError.message}` };
  }

  // 2. Create the first message in mensajes_incidencia
  const { error: msgError } = await supabaseAdmin
    .from('mensajes_incidencia')
    .insert({
      incidencia_id: incidencia.id,
      usuario_id: context.userId,
      mensaje,
      fecha: new Date().toISOString(),
    });

  if (msgError) {
    return { success: false, error: `Incidencia creada (${incidencia.id}) pero error al guardar el mensaje: ${msgError.message}` };
  }

  return {
    success: true,
    data: {
      id: incidencia.id,
      estado: 'nueva',
      mensaje_resumen: mensaje.length > 120 ? mensaje.substring(0, 120) + '...' : mensaje,
    },
  };
}
